/* ---------------------------------------------------------------------------
   /platform/showcase — media manifest.

   Ported from the Next.js site's app/platform/showcase/media.ts. This is the
   ONLY file anyone edits to put real assets on the showcase page — no code
   change, no import, no build step: drop the files under public/media/showcase/
   and list their paths in the arrays below.

   ── WHERE FILES GO ────────────────────────────────────────────────────────
   public/media/showcase/<slug>/01.mp4 … 04.mp4   video tiles (2×2 grid)
   public/media/showcase/<slug>/01.jpg            poster, same basename as clip
   public/media/showcase/<slug>/slide-01.jpg …    slider frames
   public/media/showcase/<slug>/brochure.pdf
   public/media/showcase/<slug>/deck.pdf

   Until a slug has real files, the page renders a labelled placeholder naming
   the exact path it is waiting for, and both download buttons render
   disabled rather than link to a 404.
--------------------------------------------------------------------------- */

export const VIDEO_TILES = 4;
export const SLIDER_FRAMES = 3;

export function mediaDir(slug) {
  return `/media/showcase/${slug}`;
}

export function expectedVideoPath(slug, n) {
  return `${mediaDir(slug)}/${String(n).padStart(2, "0")}.mp4`;
}

export function expectedImagePath(slug, n) {
  return `${mediaDir(slug)}/slide-${String(n).padStart(2, "0")}.jpg`;
}

const YT_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

export function youTubeId(src) {
  const m = typeof src === "string" ? src.match(YT_RE) : null;
  return m ? m[1] : null;
}

export function posterFor(videoPath) {
  const yt = youTubeId(videoPath);
  if (yt) return `https://img.youtube.com/vi/${yt}/hqdefault.jpg`;
  return videoPath.replace(/\.(mp4|webm|mov)$/i, ".jpg");
}

/* Every product starts with no media and no documents — the current state
   of the world. Fill `videos` / `images` / `brochure` / `deck` as assets land. */
const p = (slug, name, blurb) => ({
  slug,
  name,
  blurb,
  videos: [],
  images: [],
  brochure: undefined,
  deck: undefined,
});

export const SHOWCASE_TABS = [
  {
    id: "viso-yard",
    label: "Viso Yard",
    products: [
      p("container-vision", "Container Vision", "Damage survey from the CCTV you already own — every defect typed, dimensioned and located, with a report in under a minute."),
      p("tank-vision", "Tank Vision", "Continuous tank-health inspection: corrosion, deformation and surface condition tracked between checkpoints."),
      p("gate-vision", "Gate Vision", "Identity at the gate in motion — container and ISO codes, plates and seals read as the truck rolls through."),
      p("yard-vision", "Yard Vision", "One survey, then a live twin: every box's location on the stack kept current without a manual walk."),
      p("crane-vision", "Crane Vision", "Chain of custody at the lift — every move captured, time-stamped and attached to the box it belongs to."),
      p("cargo-vision", "Cargo Vision", "Counts you can prove: cases and pieces tallied on the move with the video evidence attached to the number."),
      p("document-vision", "Document Vision", "Bills of lading and paperwork in, structured key-value data out, with the source frame kept alongside."),
      p("work-vision", "Work Vision", "Attendance and activity read straight from the cameras — no readers, no badges, no new hardware."),
      p("secure-vision", "Secure Vision", "Zones, alerts and an auditable log — perimeter and safety events raised the moment they happen."),
    ],
  },
  {
    id: "viso-warehouse",
    label: "Viso Warehouse",
    products: [
      p("cargo-vision", "Cargo Vision", "Counts you can prove: cases and pieces tallied on the move with the video evidence attached to the number."),
      p("audit-vision", "Audit Vision", "Continuous stock and process audit against the record, so discrepancies surface as they occur."),
      p("dimension-vision", "Dimension Vision", "Dimensional capture from the camera — length, width, height and volume without a manual measure."),
      p("document-vision", "Document Vision", "Bills of lading and paperwork in, structured key-value data out, with the source frame kept alongside."),
      p("work-vision", "Work Vision", "Attendance and activity read straight from the cameras — no readers, no badges, no new hardware."),
      p("secure-vision", "Secure Vision", "Zones, alerts and an auditable log — perimeter and safety events raised the moment they happen."),
    ],
  },
  {
    id: "viso-factory",
    label: "Viso Factory",
    products: [
      p("production-vision", "Production Vision", "Line output, cycle time and stoppages measured from the cameras already watching the floor."),
      p("audit-vision", "Audit Vision", "Continuous stock and process audit against the record, so discrepancies surface as they occur."),
      p("dimension-vision", "Dimension Vision", "Dimensional capture from the camera — length, width, height and volume without a manual measure."),
      p("work-vision", "Work Vision", "Attendance and activity read straight from the cameras — no readers, no badges, no new hardware."),
      p("secure-vision", "Secure Vision", "Zones, alerts and an auditable log — perimeter and safety events raised the moment they happen."),
    ],
  },
  {
    id: "viso-data",
    label: "Viso Data",
    products: [
      p("compression-ai", "Compression AI", "Footage kept at a fraction of the storage, with the detail the vision models need left intact."),
      p("trace-ai", "Trace AI", "Follow an object or a person across cameras and across time, as one continuous track."),
      p("detect-ai", "Detect AI", "The general detection layer the environment products are built on, available on its own."),
      p("sports-vision", "Sports Vision", "Cricket, badminton and tennis practice sessions turned into a coaching report — ball tracking, biomechanics and auto-clipped highlights from a single standard camera, no sensors, no wearables, delivered in under 10 minutes."),
    ],
  },
  {
    // Deliberately empty — do not invent names. Renders the empty state.
    id: "previous-products",
    label: "Previous Products",
    products: [],
  },
];

const YT = {
  "container-vision": [
     "https://www.youtube.com/watch?v=uYfU0mvIMlk",
    "https://www.youtube.com/watch?v=eOzgkj75slI",
    "https://www.youtube.com/watch?v=indpYpdBc1I", 
    "https://www.youtube.com/watch?v=72zn4pWmXVA", 
   
  ],
  "gate-vision": [
    "https://www.youtube.com/watch?v=mnSIvFvfjOk", // Smart Port & Container Terminal Automation
  ],
  "yard-vision": [
    "https://www.youtube.com/watch?v=0LtbC5O4ogk", // Digital Twin | CFS
    undefined, // slot intentionally blank — previous video was removed from YouTube
  ],
  "crane-vision": [
     "https://www.youtube.com/watch?v=z-JAMJjW59c",
     "https://www.youtube.com/watch?v=_R91bsCy2lo",
     
  ],
  "cargo-vision": [
    "https://www.youtube.com/watch?v=JOfe3-sSXaY", // Smart Package Detection & Counting
    "https://www.youtube.com/watch?v=37xyDo2yFDg", // Automating Wheat Bag Counting
    "https://www.youtube.com/watch?v=IOgPqVFIR9g", // AI-Powered Sugar Bag Counting on Conveyor
     "https://youtu.be/cZP6p0isKgc",
  ],
   
  "secure-vision": [
    "https://www.youtube.com/watch?v=x60uEXqpNyk", // Crowd Gathering Alert
     "https://www.youtube.com/watch?v=Iyrtqxq3UoQ",  //PPE & headgear detection 
     undefined //slot intentionally blank — previous video was removed from YouTube
  ],
};

const IMAGES = {
  "container-vision": [
    "/media/showcase/container-vision/slide-01.jpg",
    "/media/showcase/container-vision/slide-02.jpg",
    "/media/showcase/container-vision/slide-03.jpg",
  ],
   "gate-vision": [
   "/media/showcase/gate-vision/slide-01.jpg",
   "/media/showcase/gate-vision/slide-02.jpg",
   "/media/showcase/gate-vision/slide-03.jpg", 
   ],
   "yard-vision": [
   "/media/showcase/yard-vision/slide-01.jpg",
   "/media/showcase/yard-vision/slide-02.jpg",
   "/media/showcase/yard-vision/slide-03.jpg", 
   ],
   "crane-vision": [
   "/media/showcase/crane-vision/slide-01.jpg",
   "/media/showcase/crane-vision/slide-02.jpg",
   "/media/showcase/crane-vision/slide-03.jpg", 
   ], 
   "cargo-vision": [
   "/media/showcase/cargo-vision/slide-01.jpg",
   "/media/showcase/cargo-vision/slide-02.jpg",
   "/media/showcase/cargo-vision/slide-03.jpg", 
   ],
   "work-vision": [
   "/media/showcase/work-vision/slide-01.jpg",
   "/media/showcase/work-vision/slide-02.jpg",
   "/media/showcase/work-vision/slide-03.jpg", 
   ],
   "secure-vision": [
   "/media/showcase/secure-vision/slide-01.jpg",
   "/media/showcase/secure-vision/slide-02.jpg",
   "/media/showcase/secure-vision/slide-03.jpg", 
   ]
};


const BROCHURES = {
  "audit-vision": "/media/showcase/audit-vision/brochure.pdf",
  "container-vision": "/media/showcase/container-vision/brochure.pdf",
  "crane-vision": "/media/showcase/crane-vision/brochure.pdf",
  "document-vision": "/media/showcase/document-vision/brochure.pdf",
  "gate-vision": "/media/showcase/gate-vision/brochure.pdf",
  "production-vision": "/media/showcase/production-vision/brochure.pdf",
  "secure-vision": "/media/showcase/secure-vision/brochure.pdf",
  "tank-vision": "/media/showcase/tank-vision/brochure.pdf",
  "work-vision": "/media/showcase/work-vision/brochure.pdf",
  "yard-vision": "/media/showcase/yard-vision/brochure.pdf",
};

for (const tab of SHOWCASE_TABS) {
  for (const prod of tab.products) {
    if (YT[prod.slug]) prod.videos = YT[prod.slug];
    if (IMAGES[prod.slug]) prod.images = IMAGES[prod.slug];

    if (BROCHURES[prod.slug]) {
      prod.brochure = BROCHURES[prod.slug];
    }
  }
}

/* ---------------------------------------------------------------------------
   PREVIOUS PRODUCTS — earlier ventures under the same parent (VisionXcel
   Technologies), not part of the current environment/model line above.
   Deliberately limited data per Pramod's request 2026-08-31: name, a short
   blurb, the product's own website, its own contact, and a single video
   slot rather than the full grid+slider+brochure/deck treatment.
--------------------------------------------------------------------------- */
const prev = (slug, name, blurb, website, contact) => ({
  slug,
  name,
  blurb,
  website,
  contact,
  video: undefined,
});

export const PREVIOUS_PRODUCTS = [
  prev(
    "checko",
    "Checko",
    "Anti-counterfeiting labels that can't be copied — a patented 3D mark plus a 2D cryptographic code, verified with a phone scan, with warranty and loyalty data built in.",
    "https://checko.ai",
    "Contact@transpacks.co",
  ),
  prev(
    "upjao",
    "Upjao",
    "Grain quality read from a photo — fungus, damage, moisture and a dozen other defects graded in 30 seconds, no lab required.",
    "https://upjao.ai",
    undefined,
  ),
  prev(
    "billiontests",
    "BillionTests",
    "Ten health parameters from one urine strip, read by any phone camera in 20 seconds at lab-grade accuracy — no cold chain, no fasting, no clinic.",
    "https://billiontests.ai",
    "mpsethi@billiontests.ai",
  ),
];

const PREV_YT = {
  checko: "https://www.youtube.com/watch?v=EyScMmHexFs",
  upjao: "https://www.youtube.com/watch?v=S9Un2Z1q2ew",
  billiontests: "https://www.youtube.com/watch?v=XXXXXXXXXXX",
};

for (const prod of PREVIOUS_PRODUCTS) {
  if (PREV_YT[prod.slug]) prod.video = PREV_YT[prod.slug];
}
